import React, { useEffect, useState, useRef } from "react";
import { ViewState } from "@devexpress/dx-react-scheduler";
import {
  Scheduler,
  WeekView,
  Appointments,
  AppointmentTooltip,
  DateNavigator,
  Toolbar,
  ViewSwitcher,
  DayView,
  MonthView,
  AllDayPanel,
} from "@devexpress/dx-react-scheduler-material-ui";
import capitalize from "lodash/capitalize";

import { Paper, IconButton, useMediaQuery } from "@mui/material";

import { DriverScheduleEvent } from "types";
import { Appointment, AppointmentContent } from "./components";
import { CalendarIcon } from "globals/design-system/icons";
import { moovsBlue } from "globals/design-system/colors";
import theme from "globals/design-system/theme";
import { formatDateTimeToDisplay } from "globals/utils/dateFormatters";

export type CurrentViewName = "Week" | "Month" | "Day";

type DriverAvailabilityCalendarProps = {
  selectedDate: Date;
  driverScheduleEvents: DriverScheduleEvent[];
  onSelectedDateChange?: (date: Date) => void;
  currentViewName?: CurrentViewName;
  onCurrentViewNameChange?: (viewName: CurrentViewName) => void;
  onEventClick?: (event: DriverScheduleEvent) => void;
  onDateCellClick?: (date: Date) => void;
};

// Custom calendar button component
const CalendarOpenButton = (props: DateNavigator.OpenButtonProps) => {
  const { onVisibilityToggle, ...restProps } = props;

  return (
    <IconButton {...restProps} size="small" onClick={onVisibilityToggle}>
      <CalendarIcon color={moovsBlue} />
    </IconButton>
  );
};

// Custom month cell component with click handler
const CustomMonthCell =
  (onDateCellClick: (date: Date) => void, cellHeight: number) =>
  (props: MonthView.TimeTableCellProps) => {
    const { startDate, ...restProps } = props;

    const handleClick = () => {
      if (onDateCellClick && startDate) {
        onDateCellClick(startDate);
      }
    };

    return (
      <MonthView.TimeTableCell
        {...restProps}
        startDate={startDate}
        onClick={handleClick}
        style={{
          cursor: onDateCellClick ? "pointer" : "default",
          height: cellHeight,
        }}
      />
    );
  };

// Custom week/day cell component with click handler for time slots
const CustomTimeTableCell =
  (onDateCellClick: (date: Date) => void) =>
  (props: WeekView.TimeTableCellProps) => {
    const { startDate, ...restProps } = props;

    const handleClick = () => {
      if (onDateCellClick && startDate) {
        onDateCellClick(startDate);
      }
    };

    return (
      <WeekView.TimeTableCell
        {...restProps}
        startDate={startDate}
        onClick={handleClick}
        style={{ cursor: onDateCellClick ? "pointer" : "default" }}
      />
    );
  };

function DriverAvailabilityCalendar(props: DriverAvailabilityCalendarProps) {
  const {
    selectedDate,
    driverScheduleEvents,
    onSelectedDateChange,
    currentViewName = "Week",
    onCurrentViewNameChange,
    onEventClick,
    onDateCellClick,
  } = props;

  // hooks
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const containerRef = useRef<HTMLDivElement>(null);

  // state
  const [monthCellHeight, setMonthCellHeight] = useState(100);

  // Calculate dynamic cell height for month view
  useEffect(() => {
    const calculateCellHeight = () => {
      if (containerRef.current && currentViewName === "Month") {
        const containerHeight = containerRef.current.clientHeight;
        // Subtract space for toolbar, header, navigation
        const availableHeight = containerHeight - 80;
        // Month view typically has 5-6 rows
        const rowCount = 6;
        const calculatedHeight = Math.max(
          80,
          Math.floor(availableHeight / rowCount)
        );

        setMonthCellHeight(calculatedHeight);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(calculateCellHeight, 100);

    window.addEventListener("resize", calculateCellHeight);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", calculateCellHeight);
    };
  }, [currentViewName]);

  // shape data for scheduler
  const schedulerData = driverScheduleEvents.map(
    (event: DriverScheduleEvent) => {
      // format dates to display
      const startDate = formatDateTimeToDisplay(event.startDateTime);
      const endDate = formatDateTimeToDisplay(event.endDateTime);

      return {
        id: event.id,
        title: capitalize(event.scheduleType),
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
        type: event.scheduleType,
        daysToRepeat: event.driverScheduleSetting.daysToRepeat,
        originalEvent: event, // Store original event for click handler
      };
    }
  );

  return (
    <Paper
      ref={containerRef}
      style={{ height: "100%", marginBottom: 0 }}
      elevation={0}
    >
      <Scheduler data={schedulerData} height="auto">
        <ViewState
          currentDate={selectedDate}
          onCurrentDateChange={onSelectedDateChange}
          currentViewName={currentViewName}
          onCurrentViewNameChange={onCurrentViewNameChange}
        />
        <DayView
          startDayHour={0}
          endDayHour={24}
          timeTableCellComponent={CustomTimeTableCell(onDateCellClick)}
        />
        <WeekView
          startDayHour={0}
          endDayHour={24}
          timeTableCellComponent={CustomTimeTableCell(onDateCellClick)}
        />
        <MonthView
          timeTableCellComponent={CustomMonthCell(
            onDateCellClick,
            monthCellHeight
          )}
        />
        <Toolbar />
        <DateNavigator
          {...(isMobileView ? { openButtonComponent: CalendarOpenButton } : {})}
        />
        <ViewSwitcher />
        <AllDayPanel />
        <Appointments
          appointmentComponent={(props: Appointments.AppointmentProps) => (
            <Appointment {...props} onClick={onEventClick} />
          )}
          appointmentContentComponent={AppointmentContent}
        />
        <AppointmentTooltip showCloseButton />
      </Scheduler>
    </Paper>
  );
}

export default DriverAvailabilityCalendar;
